import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAnalysisList } from "@/hooks/useAnalysis";
import { useCaseSearch } from "@/hooks/useCases";
import { useSampleList } from "@/hooks/useSamples";
import type { Case } from "@/types";

const statusLabels: Record<string, string> = {
  open: "접수",
  in_progress: "진행중",
  pending: "보류",
  closed: "종결",
};

const caseTypeLabels: Record<string, string> = {
  fire: "화재",
  arson: "방화",
  terror: "테러",
  leak: "가스누출",
  death: "사망",
  other: "기타",
};

const statusColor = (status: string) => {
  switch (status) {
    case "open":
      return "text-blue-700 bg-blue-100";
    case "in_progress":
      return "text-yellow-700 bg-yellow-100";
    case "closed":
      return "text-green-700 bg-green-100";
    default:
      return "text-gray-700 bg-gray-100";
  }
};

const analysisStatusLabels: Record<string, string> = {
  pending: "대기",
  running: "실행중",
  completed: "완료",
  failed: "실패",
};

const analysisStatusColor = (status: string) => {
  switch (status) {
    case "pending":
      return "text-gray-700 bg-gray-100";
    case "running":
      return "text-yellow-700 bg-yellow-100";
    case "completed":
      return "text-green-700 bg-green-100";
    case "failed":
      return "text-red-700 bg-red-100";
    default:
      return "text-gray-700 bg-gray-100";
  }
};

const analysisTypeLabels: Record<string, string> = {
  pattern_matching: "패턴 매칭",
  compound_inference: "화합물 추론",
  hazard_assessment: "위험성 평가",
  full_analysis: "전체 분석",
};

const sampleStatusLabels: Record<string, string> = {
  registered: "등록",
  uploaded: "업로드",
  analyzing: "분석중",
  analyzed: "분석완료",
  failed: "실패",
};

const sampleStatusColor = (status: string) => {
  switch (status) {
    case "registered":
      return "text-gray-700 bg-gray-100";
    case "uploaded":
      return "text-blue-700 bg-blue-100";
    case "analyzing":
      return "text-yellow-700 bg-yellow-100";
    case "analyzed":
      return "text-green-700 bg-green-100";
    case "failed":
      return "text-red-700 bg-red-100";
    default:
      return "text-gray-700 bg-gray-100";
  }
};

const SEARCH_TABS = [
  { key: "cases", label: "사건" },
  { key: "samples", label: "시료" },
  { key: "analyses", label: "분석" },
] as const;

type SearchTab = (typeof SEARCH_TABS)[number]["key"];

export default function SearchPage() {
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeTab, setActiveTab] = useState<SearchTab>("cases");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const {
    data: caseResults,
    isLoading: casesLoading,
    error: casesError,
  } = useCaseSearch(debouncedQuery);

  const { data: sampleResults, isLoading: samplesLoading } = useSampleList();

  const { data: analysisResults, isLoading: analysesLoading } =
    useAnalysisList();

  // Client-side filtering for samples and analyses since no dedicated search endpoint exists
  const filteredSamples = debouncedQuery
    ? sampleResults?.filter(
        (s) =>
          s.name.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
          s.sample_id.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
          (s.description ?? "")
            .toLowerCase()
            .includes(debouncedQuery.toLowerCase()),
      )
    : [];

  const filteredAnalyses = debouncedQuery
    ? analysisResults?.filter(
        (a) =>
          String(a.id).includes(debouncedQuery) ||
          a.analysis_type
            .toLowerCase()
            .includes(debouncedQuery.toLowerCase()) ||
          a.status.toLowerCase().includes(debouncedQuery.toLowerCase()),
      )
    : [];

  const isLoading =
    activeTab === "cases"
      ? casesLoading
      : activeTab === "samples"
        ? samplesLoading
        : analysesLoading;

  const error = activeTab === "cases" ? casesError : null;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">통합 검색</h1>
        <p className="text-gray-600">
          사건, 시료, 분석을 통합 검색할 수 있습니다.
        </p>
      </div>

      {/* 검색 입력 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-2">
            검색어
          </span>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="사건번호, 시료명, 분석 ID 등을 입력하세요"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
          />
        </label>
      </div>

      {/* 탭 + 결과 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* 탭 */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px px-6">
            {SEARCH_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-4 border-b-2 font-medium text-sm cursor-pointer ${
                  activeTab === tab.key
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* 검색어 미입력 상태 */}
        {!debouncedQuery && (
          <div className="p-12 text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              검색어를 입력하세요
            </p>
            <p className="text-gray-500">
              사건번호, 시료명, 분석 ID 등으로 검색할 수 있습니다.
            </p>
          </div>
        )}

        {/* 로딩 */}
        {debouncedQuery && isLoading && (
          <div className="p-6 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded mb-3" />
            ))}
          </div>
        )}

        {/* 에러 */}
        {debouncedQuery && error && (
          <div className="p-6 text-center">
            <p className="text-red-600 font-medium">
              검색 중 오류가 발생했습니다.
            </p>
            <p className="text-red-500 text-sm mt-1">
              {(error as Error).message}
            </p>
          </div>
        )}

        {/* 사건 결과 */}
        {debouncedQuery && !isLoading && !error && activeTab === "cases" && (
          <>
            {caseResults && caseResults.length > 0 ? (
              <>
                <div className="px-6 py-4 border-b border-gray-200">
                  <p className="text-sm text-gray-500">
                    사건 {caseResults.length}건 검색됨
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          사건번호
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          제목
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          유형
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상태
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          장소
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {caseResults.map((c: Case) => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              to={`/cases/${c.id}`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800"
                            >
                              {c.case_number}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {c.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {caseTypeLabels[c.case_type] ?? c.case_type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor(c.status)}`}
                            >
                              {statusLabels[c.status] ?? c.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {c.location ?? "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="p-12 text-center">
                <p className="text-gray-500">
                  사건 검색 결과가 없습니다.
                </p>
              </div>
            )}
          </>
        )}

        {/* 시료 결과 */}
        {debouncedQuery && !isLoading && activeTab === "samples" && (
          <>
            {filteredSamples && filteredSamples.length > 0 ? (
              <>
                <div className="px-6 py-4 border-b border-gray-200">
                  <p className="text-sm text-gray-500">
                    시료 {filteredSamples.length}건 검색됨
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          시료 ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          이름
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          유형
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상태
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredSamples.map((s) => (
                        <tr key={s.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              to={`/samples/${s.id}`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800"
                            >
                              {s.sample_id}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {s.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {s.sample_type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${sampleStatusColor(s.status)}`}
                            >
                              {sampleStatusLabels[s.status] ?? s.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="p-12 text-center">
                <p className="text-gray-500">
                  시료 검색 결과가 없습니다.
                </p>
              </div>
            )}
          </>
        )}

        {/* 분석 결과 */}
        {debouncedQuery && !isLoading && activeTab === "analyses" && (
          <>
            {filteredAnalyses && filteredAnalyses.length > 0 ? (
              <>
                <div className="px-6 py-4 border-b border-gray-200">
                  <p className="text-sm text-gray-500">
                    분석 {filteredAnalyses.length}건 검색됨
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          분석 유형
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상태
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          작업
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAnalyses.map((a) => (
                        <tr key={a.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              to={`/analyses/${a.id}`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800"
                            >
                              #{a.id}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {analysisTypeLabels[a.analysis_type] ??
                              a.analysis_type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${analysisStatusColor(a.status)}`}
                            >
                              {analysisStatusLabels[a.status] ?? a.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <Link
                              to={`/analyses/${a.id}/dashboard`}
                              className="text-green-600 hover:text-green-800 font-medium"
                            >
                              대시보드
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="p-12 text-center">
                <p className="text-gray-500">
                  분석 검색 결과가 없습니다.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
